import { mapEntity } from '@api';
import type { HitTestResult } from '@chartv2';
import { LegendPrice } from '@terminal/views/turbo-chart/components/legend-price/LegendPrice';
import type { TerminalChartAssetData } from '@terminal/views/turbo-chart/utils/chart-utils/types';

export interface SeriesLegendStatusProps {
  asset: TerminalChartAssetData;
  isPrimary: boolean;
  legendColor: string;
  hoverHit?: HitTestResult;
}

/**
 * Right-hand side of the chartv2 legend row. Delegates to the turbo
 * chart's `LegendPrice` — which already chooses between a
 * `LegendLivePrice` and a `LastClosePrice` view, subscribes to live
 * prices, and formats currency suffixes.
 *
 * When `hoverHit` is provided (from the chart engine's crosshair
 * hit-test), the displayed price tracks the hovered data point.
 */
export const SeriesLegendStatus = ({
  asset,
  isPrimary,
  legendColor,
  hoverHit,
}: SeriesLegendStatusProps) => {
  const { entity, snakeMeta, ts, seriesType } = asset;

  if (!snakeMeta || ts.length === 0) {
    return null;
  }

  const mappedEntity = mapEntity(entity);

  const createHoverData = (hoverHit: HitTestResult) => {
    const { point } = hoverHit;
    const time = new Date(hoverHit.point.time).toISOString();
    const currentPrice =
      'close' in point
        ? { ...point, time }
        : {
            time,
            open: point.value,
            high: point.value,
            low: point.value,
            close: point.value,
          };

    return {
      currentPrice,
    };
  };

  return (
    <LegendPrice
      entity={mappedEntity}
      snakeMeta={snakeMeta}
      ts={ts}
      legendColor={legendColor}
      isPrimary={isPrimary}
      isChartHovered={!!hoverHit}
      selectedSeriesType={seriesType}
      hoverData={hoverHit && createHoverData(hoverHit)}
    />
  );
};
